import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Search, X } from 'lucide-react';
import { SelectField } from './FormControls';

export type SortDirection = 'ascending' | 'descending';
export type DataColumn<T> = {
  id: string;
  header: string;
  accessor: (row: T) => string;
  cell?: (row: T) => ReactNode;
  sortable?: boolean;
  hideable?: boolean;
  className?: string;
};

export function SearchField({
  value,
  onChange,
  label = 'Buscar registros técnicos',
  placeholder = 'Buscar en la tabla…',
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
}) {
  return (
    <label className="ui-table-search">
      <span className="sr-only">{label}</span>
      <Search aria-hidden="true" />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
      {value ? (
        <button type="button" onClick={() => onChange('')} aria-label="Limpiar búsqueda">
          <X aria-hidden="true" />
        </button>
      ) : null}
    </label>
  );
}

export function DataTableFooter({ children }: { children: ReactNode }) {
  return <footer className="ui-data-table-footer">{children}</footer>;
}
export function Pagination({
  page,
  pages,
  onChange,
}: {
  page: number;
  pages: number;
  onChange: (page: number) => void;
}) {
  const nearbyPages = Array.from(new Set([1, pages, page - 2, page - 1, page, page + 1, page + 2]))
    .filter((candidate) => candidate >= 1 && candidate <= pages)
    .sort((a, b) => a - b);
  const paginationItems: Array<number | string> = [];
  nearbyPages.forEach((pageNumber, index) => {
    const previous = nearbyPages[index - 1];
    if (previous && pageNumber - previous > 1) paginationItems.push(`ellipsis-${previous}`);
    paginationItems.push(pageNumber);
  });
  const isPageNumber = (item: number | string): item is number => typeof item === 'number';

  return (
    <nav className="ui-pagination" aria-label="Paginación de tabla">
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        aria-label="Página anterior"
      >
        ‹
      </button>
      <span className="sr-only">
        Página <strong>{page}</strong> de {pages}
      </span>
      {paginationItems.map((item) =>
        isPageNumber(item) ? (
          <button
            key={item}
            type="button"
            className="ui-pagination__page"
            aria-label={`Página ${item}`}
            aria-current={item === page ? 'page' : undefined}
            onClick={() => onChange(item)}
          >
            {item}
          </button>
        ) : (
          <span key={item} aria-hidden="true">
            …
          </span>
        ),
      )}
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page === pages}
        aria-label="Página siguiente"
      >
        ›
      </button>
    </nav>
  );
}

export function DataTable<T extends { id: string }>({
  rows,
  columns,
  visibleColumnIds,
  sort,
  onSort,
  empty,
  paginate = true,
  pageSizeOptions = [10, 15, 25, 50],
}: {
  rows: readonly T[];
  columns: readonly DataColumn<T>[];
  visibleColumnIds: ReadonlySet<string>;
  sort: { columnId: string; direction: SortDirection };
  onSort: (columnId: string) => void;
  empty: ReactNode;
  paginate?: boolean;
  pageSizeOptions?: readonly number[];
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeOptions[1] ?? pageSizeOptions[0] ?? 15);
  const visible = columns.filter((column) => visibleColumnIds.has(column.id));
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pages);
  const displayedRows = paginate
    ? rows.slice((safePage - 1) * pageSize, safePage * pageSize)
    : rows;
  useEffect(() => setPage(1), [rows.length, pageSize]);
  if (rows.length === 0) return <>{empty}</>;
  return (
    <div className="ui-data-table-shell">
      <div className="ui-data-table__scroll" tabIndex={0} aria-label="Tabla desplazable">
        <table className="ui-data-table">
          <thead>
            <tr>
              {visible.map((column) => (
                <th key={column.id} scope="col" className={column.className}>
                  {column.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(column.id)}
                      aria-sort={sort.columnId === column.id ? sort.direction : 'none'}
                    >
                      {column.header}
                      <span aria-hidden="true">
                        {sort.columnId === column.id
                          ? sort.direction === 'ascending'
                            ? '↑'
                            : '↓'
                          : '↕'}
                      </span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedRows.map((row) => (
              <tr key={row.id}>
                {visible.map((column) => (
                  <td
                    key={column.id}
                    className={column.className}
                    data-column={column.id}
                    data-label={column.header}
                  >
                    {column.cell?.(row) ?? column.accessor(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paginate ? (
        <DataTableFooter>
          <span className="ui-results-counter">
            Mostrando {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, rows.length)}{' '}
            de {rows.length}
          </span>
          <Pagination page={safePage} pages={pages} onChange={setPage} />
          <label className="ui-data-table-page-size">
            <span className="sr-only">Registros por página</span>
            <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option} por página
                </option>
              ))}
            </select>
          </label>
        </DataTableFooter>
      ) : null}
    </div>
  );
}

type TechnicalRow = {
  id: string;
  reference: string;
  description: string;
  category: string;
  status: 'Disponible' | 'En revisión';
  updated: string;
};
export type TableEvidenceScenario = 'many' | 'few' | 'zero';
const rows: readonly TechnicalRow[] = Array.from({ length: 28 }, (_, index) => ({
  id: `row-${index + 1}`,
  reference: `UI-${String(index + 1).padStart(3, '0')}`,
  description:
    index === 2
      ? 'Texto técnico deliberadamente extenso para comprobar truncamiento, lectura estable y comportamiento responsive sin usar información de negocio.'
      : `Muestra técnica ${index + 1}`,
  category: ['Accesibilidad', 'Interacción', 'Evidencia visual'][index % 3] ?? 'Accesibilidad',
  status: index % 3 === 0 ? 'En revisión' : 'Disponible',
  updated: `2026-08-${String((index % 20) + 1).padStart(2, '0')}`,
}));

export function DataTableGallery({
  initialScenario = 'many',
}: {
  initialScenario?: TableEvidenceScenario;
}) {
  const [scenario, setScenario] = useState(initialScenario);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('Todos');
  const [category, setCategory] = useState('Todas');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection }>({
    columnId: 'reference',
    direction: 'ascending',
  });
  const [page, setPage] = useState(1);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [visible, setVisible] = useState(
    () => new Set(['reference', 'description', 'category', 'status', 'updated', 'actions']),
  );
  const [announcement, setAnnouncement] = useState('');
  const pageSize = 7;
  const columns: readonly DataColumn<TechnicalRow>[] = [
    { id: 'reference', header: 'Referencia', accessor: (row) => row.reference, sortable: true },
    {
      id: 'description',
      header: 'Descripción técnica',
      accessor: (row) => row.description,
      sortable: true,
      hideable: true,
      className: 'ui-table-long',
    },
    {
      id: 'category',
      header: 'Categoría',
      accessor: (row) => row.category,
      sortable: true,
      hideable: true,
    },
    {
      id: 'status',
      header: 'Estado',
      accessor: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <span
          className={`ui-status ui-status--${row.status === 'Disponible' ? 'ready' : 'review'}`}
        >
          {row.status}
        </span>
      ),
    },
    {
      id: 'updated',
      header: 'Actualizado',
      accessor: (row) => row.updated,
      sortable: true,
      hideable: true,
    },
    {
      id: 'actions',
      header: 'Acciones',
      accessor: () => '',
      cell: (row) => (
        <div className="ui-row-actions">
          <button
            className="ui-row-action"
            type="button"
            aria-label={`Inspeccionar ${row.reference}`}
            title="Inspeccionar"
            onClick={() => setAnnouncement(`${row.reference} inspeccionada`)}
          >
            <svg aria-hidden="true" viewBox="0 0 20 20">
              <path d="M2.5 10s2.6-4.5 7.5-4.5 7.5 4.5 7.5 4.5-2.6 4.5-7.5 4.5S2.5 10 2.5 10Z" />
              <circle cx="10" cy="10" r="2" />
            </svg>
          </button>
          <button
            className="ui-row-action"
            type="button"
            aria-label={`Más acciones para ${row.reference}`}
            title="Más acciones"
            onClick={() => setAnnouncement(`Menú técnico de ${row.reference} disponible`)}
          >
            <svg aria-hidden="true" viewBox="0 0 20 20">
              <circle cx="10" cy="4" r="1" />
              <circle cx="10" cy="10" r="1" />
              <circle cx="10" cy="16" r="1" />
            </svg>
          </button>
        </div>
      ),
    },
  ];
  const source = scenario === 'zero' ? [] : scenario === 'few' ? rows.slice(0, 3) : rows;
  const filtered = useMemo(
    () =>
      source
        .filter((row) => {
          const query = search.trim().toLocaleLowerCase('es');
          const matchesSearch =
            !query ||
            [row.reference, row.description, row.category, row.status].some((value) =>
              value.toLocaleLowerCase('es').includes(query),
            );
          const matchesStatus = status === 'Todos' || row.status === status;
          const matchesCategory = category === 'Todas' || row.category === category;
          const matchesFrom = !dateFrom || row.updated >= dateFrom;
          const matchesTo = !dateTo || row.updated <= dateTo;
          return matchesSearch && matchesStatus && matchesCategory && matchesFrom && matchesTo;
        })
        .sort((a, b) => {
          const column = columns.find((item) => item.id === sort.columnId);
          const result = (column?.accessor(a) ?? '').localeCompare(column?.accessor(b) ?? '', 'es');
          return sort.direction === 'ascending' ? result : -result;
        }),
    [category, columns, dateFrom, dateTo, search, scenario, sort, status],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pages);
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  function resetPage(action: () => void) {
    action();
    setPage(1);
  }
  function toggleColumn(id: string) {
    setVisible((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  return (
    <section className="ui-table-gallery" aria-labelledby="table-gallery-title">
      <div className="ui-table-gallery__header">
        <div>
          <span>Muestra técnica</span>
          <h2 id="table-gallery-title">Datos claros, controles precisos</h2>
          <p>Busca, filtra, ordena, pagina y adapta las columnas sin perder contexto.</p>
        </div>
        <div className="ui-scenario-switch" aria-label="Cantidad de registros">
          {(['zero', 'few', 'many'] as const).map((item) => (
            <button
              type="button"
              key={item}
              aria-pressed={scenario === item}
              onClick={() => resetPage(() => setScenario(item))}
            >
              {item === 'zero' ? 'Cero' : item === 'few' ? 'Pocos' : 'Muchos'}
            </button>
          ))}
        </div>
      </div>
      <div className="ui-filter-bar">
        <SearchField value={search} onChange={(value) => resetPage(() => setSearch(value))} />
        <div className="ui-filter-select">
          <SelectField
            label="Estado"
            value={status}
            onValueChange={(value) => resetPage(() => setStatus(value))}
            options={[
              { value: 'Todos', label: 'Todos' },
              { value: 'Disponible', label: 'Disponible' },
              { value: 'En revisión', label: 'En revisión' },
            ]}
          />
        </div>
        <div className="ui-filter-select">
          <SelectField
            label="Categoría"
            value={category}
            onValueChange={(value) => resetPage(() => setCategory(value))}
            options={[
              { value: 'Todas', label: 'Todas' },
              { value: 'Accesibilidad', label: 'Accesibilidad' },
              { value: 'Interacción', label: 'Interacción' },
              { value: 'Evidencia visual', label: 'Evidencia visual' },
            ]}
          />
        </div>
        <label className="ui-date-filter">
          <span>Desde</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => resetPage(() => setDateFrom(event.target.value))}
          />
        </label>
        <label className="ui-date-filter">
          <span>Hasta</span>
          <input
            type="date"
            value={dateTo}
            onChange={(event) => resetPage(() => setDateTo(event.target.value))}
          />
        </label>
        <button
          className="ui-clear-filters"
          type="button"
          onClick={() => {
            setSearch('');
            setStatus('Todos');
            setCategory('Todas');
            setDateFrom('');
            setDateTo('');
            setPage(1);
          }}
        >
          <svg aria-hidden="true" viewBox="0 0 20 20">
            <path d="M3 5h14M6 10h8M8 15h4" />
          </svg>
          Limpiar filtros
        </button>
        <div className="ui-columns">
          <button
            type="button"
            aria-expanded={columnsOpen}
            onClick={() => setColumnsOpen((value) => !value)}
          >
            Columnas <span aria-hidden="true">▦</span>
          </button>
          {columnsOpen ? (
            <fieldset>
              <legend>Columnas visibles</legend>
              {columns
                .filter((column) => column.hideable)
                .map((column) => (
                  <label key={column.id}>
                    <input
                      type="checkbox"
                      checked={visible.has(column.id)}
                      onChange={() => toggleColumn(column.id)}
                    />{' '}
                    {column.header}
                  </label>
                ))}
            </fieldset>
          ) : null}
        </div>
      </div>
      <div className="ui-table-meta">
        <strong>{filtered.length} registros técnicos</strong>
        <span>
          Orden: {columns.find((column) => column.id === sort.columnId)?.header} ·{' '}
          {sort.direction === 'ascending' ? 'ascendente' : 'descendente'}
        </span>
      </div>
      <DataTable
        paginate={false}
        rows={paged}
        columns={columns}
        visibleColumnIds={visible}
        sort={sort}
        onSort={(columnId) => {
          setSort((current) => ({
            columnId,
            direction:
              current.columnId === columnId && current.direction === 'ascending'
                ? 'descending'
                : 'ascending',
          }));
          setPage(1);
        }}
        empty={
          <div className="ui-table-empty">
            <strong>Sin registros para mostrar</strong>
            <p>
              {search || status !== 'Todos' || category !== 'Todas' || dateFrom || dateTo
                ? 'Ajusta la búsqueda o los filtros.'
                : 'El escenario cero está funcionando correctamente.'}
            </p>
            {search || status !== 'Todos' || category !== 'Todas' || dateFrom || dateTo ? (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setStatus('Todos');
                  setCategory('Todas');
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                Limpiar filtros
              </button>
            ) : null}
          </div>
        }
      />
      <footer className="ui-table-footer">
        <span>
          Mostrando {paged.length} de {filtered.length}
        </span>
        <Pagination page={currentPage} pages={pages} onChange={setPage} />
      </footer>
      <p className="sr-only" role="status">
        {announcement}
      </p>
    </section>
  );
}
